import { Component, OnDestroy } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { setEditAll, setValidationOrdersToGenerate } from 'src/app/redux/actions/order.action';
import { WAY_PAY_VOID } from '@shared-logic/payments';
import { appState } from 'src/app/shared/interfaces/appState.interface';
import { Order } from '@shared-logic/orders';
import { OrderValidator } from 'src/app/shared/order';
import { OrderExchangeValidator } from 'src/app/shared/order-exchange';
import { PriceValidator } from 'src/app/shared/price';
import { FormComponent } from '../form.component';
import { SessionChecker } from 'src/app/shared/session.checker';
import { ToFixValidator } from 'src/app/shared/to-fix';
import { CONDITIONS_TYPE_TO_FIX } from 'src/app/shared/dict/condition-and-indicators';
import { conditionType } from 'src/app/shared/const/options';
import {
  PurchaseMethodDefaultClassification, PURCHASE_METHOD_RESPONSE
} from '@shared-logic/integration-ibm';
import { mapBuyerPurchaseMethodDefault } from '@shared-logic/purchase-method';
import { PURCHASE_METHOD_GATE_PENDING } from 'src/app/shared/interfaces/order.interface';
import { isCramdownProductMismatch } from '@shared-logic/subjects/buyers-validations';

interface PurchaseMethodEditState {
  buyerCode: number;
  purchaseMethod: number;
}

@Component({
  selector: 'app-form-validations',
  templateUrl: './form.component.html',
  styleUrls: ['./../form.component.scss']
})
export class FormValidationComponent extends FormComponent implements OnDestroy {
  indexToEdit: number = -1;
  orderToEdit!: Order;
  isGenerateToFixValidator: boolean = false;

  private orderToForm$!: Subscription;
  private generateToFix$!: Subscription;
  /* This screen's own ordersEdit snapshot, kept only to write a resolved COMODEF
  default back into the order at indexToEdit (writePurchaseMethodToEditedOrder). */
  private ordersEdit: Order[] = [];
  /* This screen's own dedup slot (R10/R5), separate from the base class's
  lastStatePurchaseMethod: that one tracks the creation order, this one the order the
  operator is editing here, so switching between the two contexts cannot cross-talk. */
  private lastStatePurchaseMethodEdit: PurchaseMethodEditState | null = null;
  /* The buyer a default lookup was already fired for (successfully or not), so
  order.edit re-emitting on an unrelated field edit doesn't re-query COMODEF for the
  same buyer over and over while the field stays empty. Reset on buyer change; a retry
  is the only way to force it again for the same buyer (R9). */
  private lastQueriedBuyerForDefault: number | null = null;
  private purchaseMethodEditDefaultRequestId = 0;
  /* Distinguishes "Autocompletando..." from "Validando..." during the shared PENDING
  status, same as add-buyer's own field of the same name. */
  public isAutocompletingPurchaseMethod: boolean = false;

  constructor(store: Store<appState>, orderExcV: OrderExchangeValidator,
    orderV: OrderValidator, priceV: PriceValidator, dialog: MatDialog, checker: SessionChecker,
    private toFixV: ToFixValidator) {
    super(store, checker, orderExcV, orderV, priceV, dialog);
    this.orderToForm$ = this.store.select('order', 'edit').subscribe(edit => {
      this.indexToEdit = edit.indexToEdit;
      this.ordersEdit = edit.ordersEdit;
      const order = edit.ordersEdit[this.indexToEdit];
      if (!order) {
        return;
      }

      const normalizedOrder = this.normalizeOrderCramdown(order);
      this.orderToEdit = normalizedOrder;
      if (normalizedOrder !== order) {
        const orders = [...edit.ordersEdit];
        orders[this.indexToEdit] = normalizedOrder;
        this.store.dispatch(setValidationOrdersToGenerate({ orders }));
      }

      this.resolvePurchaseMethodForEdit(this.orderToEdit);
    })

    this.generateToFix$ = this.store.select('options', 'board', 'conditionTypeSelected').subscribe(conditionTypeSelected => {
      this.isGenerateToFixValidator = conditionTypeSelected == conditionType.TO_FIX;
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.orderToForm$?.unsubscribe();
    this.generateToFix$?.unsubscribe();
  }

  /* R10: this screen drives purchase-method resolution/validation from its own
  order.edit subscription above, not from the creation slice the base class tracks. */
  protected override purchaseMethodFollowsCreationOrder(): boolean {
    return false;
  }

  /* Autocompletes (COMODEF) and validates (VALCOMO) against the order the operator is
  editing right now -- the fix for finding 02, where these used to run against the
  creation slice instead. Never iterates ordersEdit: with the whole batch normally
  sharing one buyer, this fires once per distinct pair, same as add-buyer (R6). */
  private resolvePurchaseMethodForEdit(order: Order): void {
    const buyerCode = order.price?.observations?.buyer ?? 0;
    const purchaseMethod = order.purchaseMethod ?? 0;

    if (buyerCode <= 0) {
      this.lastStatePurchaseMethodEdit = null;
      this.lastQueriedBuyerForDefault = null;
      this.purchaseMethodStatus = null;
      this.purchaseMethodMessageError = null;
      return;
    }

    if (purchaseMethod <= 0) {
      /* Dedup against the buyer alone, not the pair: order.edit re-emits on every
      unrelated field edit of the order being worked on, not only on buyer/purchaseMethod
      changes. Without this, an empty field (R7's "sin valor" success) would re-query
      COMODEF on every unrelated keystroke instead of once per buyer. */
      if (this.lastQueriedBuyerForDefault === buyerCode) {
        return;
      }
      this.queryDefaultPurchaseMethodForEdit(buyerCode);
      return;
    }

    const state: PurchaseMethodEditState = { buyerCode, purchaseMethod };
    if (
      this.lastStatePurchaseMethodEdit?.buyerCode === state.buyerCode &&
      this.lastStatePurchaseMethodEdit?.purchaseMethod === state.purchaseMethod
    ) {
      return;
    }

    this.lastStatePurchaseMethodEdit = state;
    this.verifyPurchaseMethod(state);
  }

  /* R7/R8: an absent default is success (field stays empty, no error); a failed lookup
  is unavailable, never invalid. Guarded by its own request id (same pattern as
  add-buyer) so a stale response cannot overwrite a newer query or a retry. */
  private queryDefaultPurchaseMethodForEdit(buyerCode: number): void {
    const requestId = ++this.purchaseMethodEditDefaultRequestId;
    /* Marked unconditionally, including on a retry: a retry must bypass
    resolvePurchaseMethodForEdit's dedup, but once this fresh attempt is in flight the
    next unrelated order.edit emission should dedup against it just the same. */
    this.lastQueriedBuyerForDefault = buyerCode;
    this.purchaseMethodStatus = PURCHASE_METHOD_GATE_PENDING;
    this.purchaseMethodMessageError = null;
    this.isAutocompletingPurchaseMethod = true;

    this.orderV.getDefaultPurchaseMethod(buyerCode).then((classification: PurchaseMethodDefaultClassification) => {
      if (requestId !== this.purchaseMethodEditDefaultRequestId) {
        return;
      }

      this.isAutocompletingPurchaseMethod = false;

      if (classification.status == PURCHASE_METHOD_RESPONSE.UNAVAILABLE) {
        this.purchaseMethodStatus = PURCHASE_METHOD_RESPONSE.UNAVAILABLE;
        return;
      }

      const defaultValue = mapBuyerPurchaseMethodDefault(classification.rawValue ?? '');
      this.purchaseMethodStatus = null;

      if (!defaultValue.hasDefault) {
        return;
      }

      /* Writing the resolved value back into order.edit re-triggers the subscription
      above with purchaseMethod now set, which calls resolvePurchaseMethodForEdit again
      and validates it -- no separate call needed here. */
      this.writePurchaseMethodToEditedOrder(defaultValue.value);
    });
  }

  private writePurchaseMethodToEditedOrder(value: number): void {
    const current = this.ordersEdit[this.indexToEdit];
    if (!current) {
      return;
    }

    const orders = [...this.ordersEdit];
    orders[this.indexToEdit] = { ...current, purchaseMethod: value };
    this.store.dispatch(setValidationOrdersToGenerate({ orders }));
  }

  /* R9: the unavailable state's only way out short of reloading, wired to this screen
  by T2.5. Bypasses the dedup slot on purpose -- a retry must always re-fire regardless
  of whether the pair "looks" unchanged. */
  retryPurchaseMethodForEdit(): void {
    const buyerCode = this.orderToEdit.price?.observations?.buyer ?? 0;
    if (buyerCode <= 0) {
      return;
    }

    const purchaseMethod = this.orderToEdit.purchaseMethod ?? 0;
    if (purchaseMethod <= 0) {
      this.queryDefaultPurchaseMethodForEdit(buyerCode);
      return;
    }

    this.lastStatePurchaseMethodEdit = { buyerCode, purchaseMethod };
    this.verifyPurchaseMethod({ buyerCode, purchaseMethod });
  }

  getSellerNameAndTons(order: Order) {
    return this.orderV.getSellerNameAndTons(order);
  }

  /* R4/R5, additive -- never replaces normalizeOrderCramdown() above, which only decides
  the Marca. Does NOT reuse FormComponent.isCramdownOrderBlocked: that one reads
  order.buyer.onlySell (the form-order "Solo le vende a" field), already reset/irrelevant
  by this stage. Here the buyer is already fixed in price.observations.buyer, same field
  resolvePurchaseMethodForEdit above reads for this same orderToEdit. Called from the
  template (form.component.html), so it's re-evaluated on every change-detection cycle
  triggered by orderToForm$ updating orderToEdit -- no separate subscription needed (R6). */
  isCramdownOrderEditBlocked(): boolean {
    const sellerCode = this.orderToEdit?.sellerData?.codeS ?? 0;
    const buyerCandidates = [this.orderToEdit?.price?.observations?.buyer ?? 0];
    const productName = this.orderToEdit?.price?.productName ?? 0;
    return isCramdownProductMismatch(sellerCode, buyerCandidates, productName);
  }

  override deleteAll(numOfStep: number): void {
  }

  haveIndicators(order: Order) {
    return this.toFixV.orderHaveIndicators(order);
  }

  override deleteStep(numOfStep: number) {
    let order: Order = JSON.parse(JSON.stringify(this.orderToEdit));
    if (numOfStep == 1) {
      order.buyer = { onlySell: -1, notSell: -1 };
      order.purchaseMethod = 0;
      order.tons = -1;
      order.quotas = "[]";
    } else if (numOfStep == 2) {
      order.price.typeBusiness = -1;
      order.price.productName = -1;
      order.price.price = -1;
      order.price.typeCoin = '--';
      order.price.placeOfDelivery = { port: -1, zone: -1 };
      order.price.quality = -1;
    } else if (numOfStep == 3) {
      order.price.deliveryPeriod = { endDate: '--', startDate: '--' };
      order.price.harvest = '--';
      order.price.wayPay = WAY_PAY_VOID;
      order.price.pesificacion = { endDate: '--', startDate: '--' }
    } else if (numOfStep == 4) {
      order.price.conditionType = 0;
      order.price.indicators = [];
    }
    this.store.dispatch(setValidationOrdersToGenerate({ orders: [order] }));
  }

  showTypeDolar(order: Order) {
    return this.orderV.isInDolars(order);
  }
}
